import { useCallback } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { updateViewState, updateMapState } from '@/features/analysisMapSlice';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { notifications } from '@mantine/notifications';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { DEFAULT_FALLBACK_ZOOM_LEVEL } from './deckglUtils';
import type { GeolocateErrorEvent } from 'react-map-gl/mapbox';
import {
  useGeolocateControl,
  GeolocateStatus,
} from '@/features/geolocateContext';

interface GeolocateEvent {
  target?: {
    _lastKnownPosition?: {
      coords?: {
        latitude: number;
        longitude: number;
      };
    };
  };
}

export const useGeolocateCallbacks = () => {
  const dispatch = useAppDispatch();
  const intl = useClientTerminology();
  const { setStatus, status } = useGeolocateControl();

  const onGeolocate = useCallback(
    (e: GeolocateEvent) => {
      // The event from react-map-gl's GeolocateControl wraps the Mapbox event
      // Access the last known position from the control instance
      // This is a workaround that should be fixed in `mapbox-gl` v3.16.0
      // https://github.com/mapbox/mapbox-gl-js/issues/13552
      // However, we cannot yet upgrade to that version due to
      // https://github.com/mapbox/mapbox-gl-js/issues/13576
      const lastPosition = e.target?._lastKnownPosition;

      if (
        lastPosition?.coords &&
        (status === GeolocateStatus.TRACKING ||
          status === GeolocateStatus.LOCATING)
      ) {
        // We have to utilize this instead of the built-in user
        // tracking from the component because Deck.gl controls
        // the view state based upon the Redux state.
        const geolocateState = {
          latitude: lastPosition.coords.latitude,
          longitude: lastPosition.coords.longitude,
          zoom: DEFAULT_FALLBACK_ZOOM_LEVEL,
        };
        dispatch(updateViewState(geolocateState));
        // Also update mapState so geolocation persists across navigation
        dispatch(updateMapState(geolocateState));

        // Transition from LOCATING to TRACKING once we have a location
        setStatus(GeolocateStatus.TRACKING);

        TagManager.dataLayer({
          dataLayer: {
            event: GoogleEvents.geolocatorSuccess,
          },
        });
      }
    },
    [dispatch, setStatus, status]
  );

  const onGeolocateError = useCallback(
    (e: GeolocateErrorEvent) => {
      const isPermissionError = e.code === 1; // 1: PERMISSION_DENIED
      notifications.show({
        title: 'Geolocation error',
        color: 'redBrand',
        message: intl.formatMessage({
          id: isPermissionError
            ? 'map.geolocation.permissionError'
            : 'map.geolocation.otherError',
        }),
      });

      TagManager.dataLayer({
        dataLayer: {
          event: isPermissionError
            ? GoogleEvents.geolocatorPermissionError
            : GoogleEvents.geolocatorOtherError,
        },
      });

      // Disable geolocation - setStatus handles triggering the control off
      setStatus(GeolocateStatus.DISABLED);
    },
    [intl, setStatus]
  );

  return {
    onGeolocate,
    onGeolocateError,
  };
};
